/**
 * WP-V2-WC — minimapa (kółko w rogu): kropki landmarków + pozycja gracza.
 * Ukryta we wnętrzach (visible=false / setInterior).
 */
import Phaser from 'phaser';
import { GAME_WIDTH } from '@pieskowo/shared';
import type { MinimapLandmark } from './types.js';

export type MinimapOpts = {
  /** Środek kółka na ekranie (scrollFactor 0). Domyślnie prawy-górny róg. */
  x?: number;
  y?: number;
  radius?: number;
  worldW: number;
  worldH: number;
  landmarks?: readonly MinimapLandmark[];
  /** false = ukryta (wnętrza). Domyślnie true. */
  visible?: boolean;
};

/** Drop landmarks outside world AABB (do not clamp them onto the minimap rim). */
export function landmarksInWorldBounds(
  landmarks: readonly MinimapLandmark[],
  worldW: number,
  worldH: number,
): MinimapLandmark[] {
  return landmarks.filter(
    (lm) =>
      lm.x >= 0 &&
      lm.x <= worldW &&
      lm.y >= 0 &&
      lm.y <= worldH,
  );
}

export class Minimap {
  readonly container: Phaser.GameObjects.Container;
  private readonly radius: number;
  private readonly worldW: number;
  private readonly worldH: number;
  private readonly playerDot: Phaser.GameObjects.Arc;
  private landmarkDots: Phaser.GameObjects.Arc[] = [];
  private interior = false;

  constructor(
    private readonly scene: Phaser.Scene,
    opts: MinimapOpts,
  ) {
    this.radius = opts.radius ?? 56;
    this.worldW = opts.worldW;
    this.worldH = opts.worldH;
    const x = opts.x ?? GAME_WIDTH - this.radius - 16;
    const y = opts.y ?? this.radius + 16;

    const bg = scene.add
      .circle(0, 0, this.radius, 0x4a3f55, 0.55)
      .setStrokeStyle(3, 0xffffff, 0.9);
    // delikatny pierścień pastelowy
    const ring = scene.add.circle(0, 0, this.radius - 4, 0xffb6c1, 0.12);

    this.playerDot = scene.add.circle(0, 0, 5, 0xff6b9d, 1).setStrokeStyle(1, 0xffffff);

    this.container = scene.add.container(x, y, [bg, ring, this.playerDot]);
    this.container.setScrollFactor(0);
    this.container.setDepth(10_000);
    this.container.setSize(this.radius * 2, this.radius * 2);

    if (opts.landmarks) {
      this.setLandmarks(opts.landmarks);
    }

    const vis = opts.visible !== false;
    this.container.setVisible(vis);
  }

  setLandmarks(landmarks: readonly MinimapLandmark[]): void {
    for (const d of this.landmarkDots) d.destroy();
    this.landmarkDots = [];
    for (const lm of landmarksInWorldBounds(
      landmarks,
      this.worldW,
      this.worldH,
    )) {
      const p = this.worldToMinimap(lm.x, lm.y);
      const dot = this.scene.add
        .circle(p.x, p.y, 4, lm.color ?? 0xbde0fe, 1)
        .setStrokeStyle(1, 0xffffff, 0.8);
      this.landmarkDots.push(dot);
      this.container.add(dot);
    }
    // gracz zawsze na wierzchu
    this.container.bringToTop(this.playerDot);
  }

  /** Aktualizuj kropkę gracza. */
  setPlayerPosition(worldX: number, worldY: number): void {
    const p = this.worldToMinimap(worldX, worldY);
    this.playerDot.setPosition(p.x, p.y);
  }

  /**
   * We wnętrzach minimapa jest ukryta (orientacja niepotrzebna).
   * Town / Garden: setInterior(false).
   */
  setInterior(interior: boolean): void {
    this.interior = interior;
    this.container.setVisible(!interior);
  }

  get isInterior(): boolean {
    return this.interior;
  }

  setVisible(v: boolean): void {
    if (this.interior) {
      this.container.setVisible(false);
      return;
    }
    this.container.setVisible(v);
  }

  private worldToMinimap(wx: number, wy: number): { x: number; y: number } {
    // Zmapuj świat na dysk o promieniu radius * 0.85 (margines od krawędzi)
    const r = this.radius * 0.85;
    const nx = this.worldW > 0 ? wx / this.worldW : 0.5;
    const ny = this.worldH > 0 ? wy / this.worldH : 0.5;
    // (0,0) świata → lewy-górny na minimapie
    let x = (nx - 0.5) * 2 * r;
    let y = (ny - 0.5) * 2 * r;
    // Clamp do koła
    const dist = Math.hypot(x, y);
    if (dist > r) {
      x = (x / dist) * r;
      y = (y / dist) * r;
    }
    return { x, y };
  }

  destroy(): void {
    this.container.destroy(true);
    this.landmarkDots = [];
  }
}
